import { styled, css, withTheme } from '@vebox/etui-system';
import { Component, createRef, createElement } from 'react';
import { c as _defineProperty, a as _objectSpread2, b as _extends } from './_rollupPluginBabelHelpers-3b7bd988.js';
import { Div } from '@vebox/etui-system-elements';
import Badge from './Badge/Badge.js';
import { format } from 'date-fns';
import { min, max, eq, inRange } from 'date-arithmetic';
import { createEvent, isDateInEventRange, isCurrentDateLess, isCurrentDateGreater } from './WeekCalendar/utils/eventHelpers.js';
import { getTimeSlots } from './WeekCalendar/utils/getTimeSlots.js';
import { getDisplayEvents } from './WeekCalendar/utils/getDisplayEvents.js';
import { getBoundsForNode, getEventNodeFromPoint, isEvent } from './WeekCalendar/utils/domHelpers.js';
import { G as GridSlotTooltip, a as GridSlotGroup } from './GridSlotGroup-ad0bba3c.js';
import TimeSelection from './WeekCalendar/TimeSelection.js';
import Event from './WeekCalendar/Event.js';

let WeekCalendarManageEventMode = /*#__PURE__*/function (WeekCalendarManageEventMode) {
  WeekCalendarManageEventMode["create"] = "create";
  WeekCalendarManageEventMode["edit"] = "edit";
  WeekCalendarManageEventMode["delete"] = "delete";
  return WeekCalendarManageEventMode;
}({});

const GridColumnDiv = styled(Div)`
  border-top: 1px solid ${({
  theme
}) => theme.palette.divider};
  display: flex;
  flex: 1;
  flex-direction: column;
  height: 100%;
  position: relative;
  user-select: none;
  width: ${({
  columnWidth
}) => columnWidth || '100%'};
`;
const EventContainer = styled(Div)`
  position: absolute;
  bottom: 0;
  left: 0;
  background-color: transparent;
  margin: 0 auto;
  top: 0;
  width: 30%;
  max-width: 40px;
`;
const NowIndicator = styled(Div)`
  display: flex;
  position: absolute;
  left: 0;
  top: ${({
  top
}) => top || '0'}%;
  transform: translateX(calc(-100% - 10px));

  > div {
    transform: translateY(-50%);
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 10px;
    height: 2px;
    transform: translate(100%, -50%);
    ${({
  theme
}) => {
  return css`
        background-color: ${theme.palette.grey[60]};
      `;
}};
  }
`;
const ColumnDivider = styled(Div)`
  position: absolute;
  width: 2px;
  height: 100%;
  border-left: 1px dashed ${({
  theme
}) => theme.palette.primary.main};
  left: -1px;
  transform: translateX(-50%);
`;

class GridColumn extends Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "domNodeRef", /*#__PURE__*/createRef());
    _defineProperty(this, "getCurrentRange", startDate => {
      return this.timeSlotGetter.getRange(startDate, new Date());
    });
    _defineProperty(this, "getSelectionState", point => {
      let currentSlot = this.timeSlotGetter.closestTimeSlotFromPoint(point, getBoundsForNode(this.domNodeRef.current));
      if (!this.state.isSelecting) this.initialSlot = currentSlot;
      const {
        initialSlot
      } = this;
      if (initialSlot === currentSlot) {
        currentSlot = this.timeSlotGetter.nextTimeSlot(initialSlot);
      }
      const selectedRange = this.timeSlotGetter.getRange(min(initialSlot, currentSlot), max(initialSlot, currentSlot));
      return _objectSpread2(_objectSpread2({}, selectedRange), {}, {
        isSelecting: true,
        top: selectedRange.top,
        height: selectedRange.height
      });
    });
    _defineProperty(this, "selectable", () => {
      // eslint-disable-next-line no-multi-assign
      const selector = this.selector = new TimeSelection(this.domNodeRef.current, {
        longPressThreshold: this.props.pressThreshold
      });
      selector.on('selecting', this.maybeSelect);
      selector.on('selectStart', eventData => {
        if (eventData.isResizeElement) {
          this.setState({
            isResizing: true
          });
        } else {
          this.setState({
            resizingEventId: null
          });
        }
        this.maybeSelect(eventData);
      });
      selector.on('beforeSelect', box => {
        const {
          isSingleTimeSelection
        } = this.props;
        const isStart = box.event.target.classList.contains('isStart');
        const isEnd = box.event.target.classList.contains('isEnd');
        const isResizeElement = isStart || isEnd;
        const eventNode = getEventNodeFromPoint(this.domNodeRef.current, box);
        const isEditable = this.isEventEditable(box, isResizeElement);
        return {
          eventNode,
          isSingleTimeSelection,
          isEditable,
          isResizeElement,
          isStart,
          isEnd
        };
      });
      selector.on('click', box => this.selectorClicksHandler(box));
      selector.on('doubleClick', box => this.selectorClicksHandler(box));
      selector.on('select', () => {
        if (!this.state.isSelecting) {
          return;
        }
        const {
          startDate,
          endDate
        } = this.state;
        const {
          events
        } = this.props;
        const eventId = this.state.resizingEventId || null;
        const otherDayEvents = events.filter(event => event.id !== this.state.resizingEventId);
        this.setState({
          isSelecting: false,
          isResizing: false,
          resizingEventId: null
        });
        if (!this.props.isSingleTimeSelection) {
          const editOverlapEvent = this.getEditOverlapEvent(otherDayEvents, startDate, endDate);
          if (editOverlapEvent) {
            const existingEvent = this.props.events.find(event => event.id === eventId);
            if (existingEvent) {
              this.props.onManageEvent(existingEvent, WeekCalendarManageEventMode.delete);
            }
            this.handleOnSelectTimeSlot(editOverlapEvent.startDate, editOverlapEvent.endDate, editOverlapEvent.id);
          }
          const newOverlapEventMeta = this.getNewOverlapEventMeta(otherDayEvents, startDate, endDate, eventId);
          if (newOverlapEventMeta.event) {
            const {
              event,
              redundantEvents
            } = newOverlapEventMeta;
            redundantEvents.forEach(redundantEvent => this.props.onManageEvent(redundantEvent, WeekCalendarManageEventMode.delete));
            this.handleOnSelectTimeSlot(event.startDate, event.endDate, event.id);
          }
        }
        this.handleOnSelectTimeSlot(startDate, endDate, eventId);
      });
      selector.on('reset', () => {
        if (this.state.isSelecting) {
          this.setState({
            isSelecting: false
          });
        }
      });
    });
    _defineProperty(this, "maybeSelect", box => {
      const {
        onSelectingTimeSlot
      } = this.props;
      const state = this.getSelectionState(box);
      const {
        startDate: start,
        endDate: end
      } = state;
      if (onSelectingTimeSlot) {
        if (eq(this.state.startDate, start, 'minutes') && eq(this.state.endDate, end, 'minutes') || !onSelectingTimeSlot(start, end)) {
          return;
        }
      }
      if (this.state.start !== state.start || this.state.end !== state.end || this.state.isSelecting !== state.isSelecting) {
        this.setState(state);
      }
    });
    _defineProperty(this, "selectorClicksHandler", box => {
      const isSomeEvent = isEvent(this.domNodeRef.current, box);
      const {
        startDate,
        endDate
      } = this.getSelectionState(box);
      const eventExistAtDate = this.eventExistAtDate(startDate);
      if (!isSomeEvent && !eventExistAtDate) {
        const eventId = null;
        if (this.props.isSingleTimeSelection) {
          this.handleOnSelectTimeSlot(startDate, startDate, eventId);
        } else {
          this.handleOnSelectTimeSlot(startDate, endDate, eventId);
        }
      }
      this.setState({
        isSelecting: false
      });
    });
    _defineProperty(this, "handleOnSelectTimeSlot", (startDate, endDate, eventId) => {
      if (this.props.onManageEvent) {
        const newEvent = createEvent(startDate, endDate, eventId);
        const mode = this.getManageEventMode(newEvent, eventId);
        if (this.props.isSingleTimeSelection) {
          newEvent.endDate = new Date(newEvent.startDate);
        }
        this.props.onManageEvent(newEvent, mode);
      }
    });
    _defineProperty(this, "teardownSelectable", () => {
      if (!this.selector) return;
      this.selector.teardown();
      this.selector = null;
    });
    _defineProperty(this, "handleOnSelectEvent", event => {
      if (this.props.onSelectEvent) {
        const {
          startDate,
          endDate
        } = event;
        const eventPosition = this.timeSlotGetter.getRange(startDate, endDate);
        this.props.onSelectEvent(event, eventPosition);
      }
    });
    _defineProperty(this, "handleEventMouseDown", event => {
      this.setState({
        selectedEvent: event,
        resizingEventId: event.id
      });
    });
    _defineProperty(this, "handleGridSlotMouseEnter", (e, timeValue) => {
      const {
        clientY: y
      } = e;
      const {
        target
      } = e;
      requestAnimationFrame(() => {
        this.setState({
          hoverCursor: {
            y,
            // @ts-ignore
            x: target
            // @ts-ignore
            .closest('.week-calendar_grid-column').getBoundingClientRect().left,
            time: format(timeValue, this.props.timeFormat)
          }
        });
      });
    });
    _defineProperty(this, "handleFocus", event => {
      const targetClassList = event.target.classList;
      if (!targetClassList.contains('event') && !targetClassList.contains('isStart') && !targetClassList.contains('isEnd')) {
        this.setState({
          isTimeTooltipVisible: true
        });
      }
    });
    _defineProperty(this, "handleGridMouseEnter", event => {
      // @ts-ignore
      const targetClassList = event.target.classList;
      if (!targetClassList.contains('event') && !targetClassList.contains('isStart') && !targetClassList.contains('isEnd')) {
        this.setState({
          isTimeTooltipVisible: true
        });
      }
    });
    _defineProperty(this, "handleGridMouseLeave", () => {
      this.setState({
        isTimeTooltipVisible: false
      });
    });
    _defineProperty(this, "renderEvents", () => {
      const {
        isResizing,
        resizingEventId
      } = this.state;
      const {
        events,
        timeFormat,
        isToday,
        isTodayDisabled
      } = this.props;
      const displayEvents = getDisplayEvents({
        events,
        timeSlotGetter: this.timeSlotGetter
      });
      return displayEvents.map(({
        event,
        style
      }, index) => {
        const currentTimePointDimensions = isToday ? this.getCurrentRange(event.startDate) : null;
        const startDate = new Date(event.startDate.getTime());
        startDate.setHours(startDate.getHours() - 1);
        const endDate = new Date(event.endDate.getTime());
        endDate.setHours(endDate.getHours() + 1);
        const someEventExistsOnStart = this.eventExistAtDate(startDate);
        const someEventExistsOnEnd = this.eventExistAtDate(endDate);
        return /*#__PURE__*/createElement(Event, _extends({}, event, {
          key: `event_${event.id}_${index}`,
          isEditing: event.id === resizingEventId && isResizing,
          style: style,
          event: event,
          onClick: this.handleOnSelectEvent,
          onMouseDown: this.handleEventMouseDown,
          onTouchStart: this.handleEventMouseDown,
          currentTimePointDimensions: currentTimePointDimensions // Only for the current Day
          ,
          isToday: isToday,
          isTodayDisabled: isTodayDisabled,
          timeFormat: timeFormat,
          isTimeStartLabelHidden: someEventExistsOnStart,
          isTimeEndLabelHidden: someEventExistsOnEnd,
          onMouseHover: this.handleGridMouseLeave
        }));
      });
    });
    _defineProperty(this, "isEditingEvent", () => {
      const {
        isResizing,
        resizingEventId
      } = this.state;
      return resizingEventId && isResizing;
    });
    const {
      min: min$1,
      max: max$1,
      timeStep
    } = this.props;
    this.timeSlotGetter = getTimeSlots({
      min: min$1,
      max: max$1,
      timeStep
    });
    this.state = {
      isSelecting: false,
      isResizing: false,
      resizingEventId: null,
      startDate: null,
      endDate: null,
      start: null,
      end: null,
      top: null,
      height: null,
      isTimeTooltipVisible: false,
      hoverCursor: {
        time: null,
        x: null,
        y: null
      }
    };
  }
  componentDidMount() {
    if (this.props.isSelectable) {
      this.selectable();
    }
  }
  componentDidUpdate(prevProps) {
    if (this.props.isSelectable && !prevProps.isSelectable) {
      this.selectable();
    }
    if (!this.props.isSelectable && prevProps.isSelectable) {
      this.teardownSelectable();
    }
    this.timeSlotGetter = this.timeSlotGetter.update(this.props);
  }
  componentWillUnmount() {
    this.teardownSelectable();
  }
  getManageEventMode(event, initialEventId) {
    const startDateEqualsEndDate = event.startDate.getTime() === event.endDate.getTime();
    if (!this.props.isSingleTimeSelection && startDateEqualsEndDate) {
      return WeekCalendarManageEventMode.delete;
    }
    return initialEventId ? WeekCalendarManageEventMode.edit : WeekCalendarManageEventMode.create;
  }
  // eslint-disable-next-line class-methods-use-this
  getEditOverlapEvent(otherDayEvents, startDate, endDate) {
    let newEvent = null;
    otherDayEvents.forEach(event => {
      const startInEventRange = isDateInEventRange(startDate, event);
      const endInEventRange = isDateInEventRange(endDate, event);
      if (startInEventRange) {
        newEvent = {
          endDate,
          startDate: event.startDate,
          id: event.id
        };
      }
      if (endInEventRange) {
        newEvent = {
          startDate,
          endDate: event.endDate,
          id: event.id
        };
      }
    });
    return newEvent;
  }

  // eslint-disable-next-line class-methods-use-this
  getNewOverlapEventMeta(otherDayEvents, startDate, endDate, eventId) {
    let newEvent = null;
    const redundantEvents = [];
    otherDayEvents.forEach(event => {
      const newStartLess = isCurrentDateLess(startDate, event.startDate);
      const newEndGreater = isCurrentDateGreater(endDate, event.endDate);
      if (newStartLess && newEndGreater) {
        newEvent = {
          startDate,
          endDate,
          id: eventId
        };
        redundantEvents.push(event);
      }
    });
    return {
      redundantEvents,
      event: newEvent
    };
  }
  isEventEditable(box, isResizeElement) {
    const {
      startDate
    } = this.getSelectionState(box);
    const isTodayEvent = box.event.target.hasAttribute('data-todayevent');
    const eventExistAtDate = this.eventExistAtDate(startDate);
    const isEventElement = isEvent(this.domNodeRef.current, box);
    if (this.state.selectedEvent && this.state.selectedEvent.disabled && isEventElement) {
      return false;
    }
    if (this.props.isSingleTimeSelection) {
      return !isResizeElement;
    }
    return !eventExistAtDate && (isResizeElement || !isEventElement || !isTodayEvent);
  }
  eventExistAtDate(newEventStartDate) {
    return this.props.events.some(dayEvent => {
      if (dayEvent.disabled) return false;
      return inRange(newEventStartDate, dayEvent.startDate, dayEvent.endDate, 'Minutes');
    });
  }
  render() {
    const {
      timeFormat,
      isToday,
      isTodayDisabled,
      theme,
      columnWidth
    } = this.props;
    const {
      isSelecting,
      top,
      height,
      startDate,
      endDate,
      isTimeTooltipVisible
    } = this.state;
    const dataNow = this.getCurrentRange(new Date());
    const tooltipPositionTop = `${this.state.hoverCursor.y - 20}px`;
    const tooltipPositionLeft = `${this.state.hoverCursor.x}px`;
    return /*#__PURE__*/createElement(GridColumnDiv, {
      testId: "week-calendar_grid-column",
      className: "week-calendar_grid-column",
      columnWidth: columnWidth,
      onFocus: this.handleFocus,
      onMouseOver: this.handleGridMouseEnter,
      onMouseLeave: this.handleGridMouseLeave,
      elRef: this.domNodeRef
    }, /*#__PURE__*/createElement(ColumnDivider, null), isTimeTooltipVisible && !isSelecting && /*#__PURE__*/createElement(GridSlotTooltip, {
      top: tooltipPositionTop,
      left: tooltipPositionLeft
    }, this.state.hoverCursor.time), this.timeSlotGetter.groups.map((group, idx) => /*#__PURE__*/createElement(GridSlotGroup, {
      key: idx,
      group: group,
      isColumn: true,
      timeFormat: timeFormat,
      onGridSlotMouseEnter: this.handleGridSlotMouseEnter
    })), isToday && !isTodayDisabled && /*#__PURE__*/createElement(NowIndicator, {
      testId: "week-calendar_now-indicator",
      top: dataNow && dataNow.height + dataNow.top
    }, /*#__PURE__*/createElement(Badge, {
      text: "Today",
      styles: {
        root: {
          bg: theme.palette.primary.main
        }
      }
    })), this.renderEvents(), isSelecting && /*#__PURE__*/createElement(Event, {
      isSelecting: isSelecting,
      style: {
        top,
        height
      },
      event: {
        startDate,
        endDate,
        id: this.isEditingEvent() ? this.state.selectedEvent.id : null
      },
      timeFormat: timeFormat,
      onNewEventRender: this.props.onNewEventRender
    }));
  }
}
_defineProperty(GridColumn, "defaultProps", {
  onSelectEvent: null,
  onManageEvent: null
});
var GridColumn$1 = withTheme(GridColumn);

export { GridColumn$1 as G, WeekCalendarManageEventMode as W };
